import React, { useMemo, useEffect, useState, useCallback } from "react"
import { Box, Grid, Heading, VStack, Button, Stack, Link, Select } from "@chakra-ui/react"
import TileCarousel from "@app/components/TileCarousel/TileCarousel"
import { useMedia } from "@app/hooks/useMedia"
import ProductShopTheLookProducts from "@app/components/Product/ProductShopTheLookProductCardImages"
import { useShopify } from "@app/hooks/useShopify"
import { useCart } from "@app/hooks/useCart"
import { useRoutes } from "@app/hooks/useRoutes"
import { useConfigContext } from "@app/providers/config"
import ProductPrice from "@app/components/Product/ProductPrice"
import ProductQuantity from "@app/components/Product/ProductQuantity"
import ProductBadges from "@app/components/Product/ProductBadges"
import ProductWishlist from "@app/components/Product/ProductWishlist"
import ProductCardDrawer from "@app/components/ProductCardDrawer"
import Image from "@app/components/Image/Image"

export interface CuratedLookProps {
  fullWidth?: boolean
  sanityProduct: any
  settings: GatsbyTypes.SanityCarouselSettings
  onSetActiveLook?: (handle: NormalisedLookProduct) => void
}

const ShopTheLook = ({ sanityProduct, section }) => {
  const carouselSettings = {
    enableScrollbar: true,
    enableNavigation: typeof window !== "undefined" ? (window?.innerWidth > 1440 ? false : true) : true,
  } as GatsbyTypes.SanityCarouselSettings
  const [product, setProduct] = useState([])

  const { isBase } = useMedia()
  const { getProducts } = useShopify()
  const related = useMemo(
    () =>
      sanityProduct?.tags
        ?.filter(tag => tag?.includes("stl:"))
        ?.map(hotspot => ({
          handle: hotspot?.split(":")[1],
        })),
    [sanityProduct]
  )

  useEffect(() => {
    const fetchProductData = async () => {
      const productData = []

      for (const handle of related) {
        const data = await getProducts({
          firstImages: 10,
          firstVariants: 10,
          handles: [handle?.handle],
        })
        productData.push(data[0])
      }

      setProduct([...productData])
    }

    fetchProductData()
  }, [related, setProduct])

  const filteredProducts = product.filter(productData => productData?.availableForSale === true)

  return (
    <Box display="flex" flexDirection="column" backgroundColor="#F3F2EE" py="64px" px={{ base: "16.5px", sm: "0" }}>
      <Heading pb="34px" textAlign="center">
        {section?.title}
      </Heading>
      <Box
        display="flex"
        flexDirection={{ base: "column", sm: "row", md: "row" }}
        pl={{ base: "0px", sm: "64px" }}
        pr={typeof window !== "undefined" ? (window?.innerWidth > 1440 ? "64px" : "0") : "0"}
        justifyContent="center"
      >
        <Box width="100%" maxWidth={{ base: "100%", sm: "646px", md: "646px" }} pr={{ base: "0", sm: "16px" }}>
          <ProductShopTheLookProducts image={section?.image} />
        </Box>

        <Box width="100%" maxWidth="750px" overflow={`hidden`} sx={{ ".swiper": { overflow: `visible` } }}>
          {isBase ? (
            <Grid templateColumns={`repeat(2, 1fr)`} gap={2}>
              {product?.map((product: any) =>
                product?.availableForSale == true ? <RelatedItem product={product} key={product?.id} /> : null
              )}
            </Grid>
          ) : (
            <TileCarousel
              carouselSettings={carouselSettings}
              spaceBetweenTiles={10}
              navigationProps={{ top: "32%", bg: "brand.primary", color: "black" }}
              slidesPerView={
                typeof window !== "undefined" ? (window?.innerWidth > 1440 ? filteredProducts?.length : filteredProducts?.length) : "auto"
              }
            >
              {filteredProducts?.map((product: any) => {
                return <RelatedItem product={product} key={product?.id} filteredProductsLength={filteredProducts?.length} />
              })}
            </TileCarousel>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default React.memo(ShopTheLook)

export const RelatedItem = ({ product, filteredProductsLength }) => {
  const iconButtonProps: BoxProps = {
    pos: "absolute",
    top: "15px",
    right: "10px",
    zIndex: 5,
  }
  const { addToCart, loading: cartLoading } = useCart()
  const {
    settings: { routes },
  } = useConfigContext()
  const { urlResolver } = useRoutes()
  const { isBase } = useMedia()

  const link = urlResolver(product, routes.PRODUCT)
  const sizes = useMemo(
    () =>
      product?.variants?.map(variant => ({
        ...variant,
        id: variant?.id,
        availableForSale: variant?.availableForSale,
        size: variant?.selectedOptions?.find(option => option?.name === "Size")?.value,
      })),
    [product]
  )
  const [selectedSize, setSelectedSize] = useState(sizes?.[0]?.id)
  const [quantity, setQuantity] = useState<number>(1)
  const [quickViewProduct, setQuickViewProduct] = useState<BundleProduct | null>(null)

  const onIncrement = (amount: number) => setQuantity(qty => qty + amount)
  const onDecrement = (amount: number) => setQuantity(qty => Math.max(1, qty - amount))

  const parsedSelectedSize = useMemo(() => sizes?.find(size => size?.id === selectedSize), [selectedSize, sizes])
  const handleAddToCart = useCallback(async () => {
    await addToCart(selectedSize, 1, [])
  }, [addToCart, selectedSize])

  const selectedProduct = product?.variants?.filter(item => item.id === selectedSize)
  return (
    <Stack key={product?.id} direction={"column"} alignItems={`center`} gap={1} position="relative">
      <ProductWishlist product={product} size={"sm"} {...iconButtonProps} display={"block"} />
      <Box maxWidth={`100%`} width={`100%`} border={`1px solid #D3D1CF`} borderRadius={`sm`} overflow={`hidden`}>
        <Image src={product?.images[0]?.src} alt={product?.images[0]?.altText} />
      </Box>
      <VStack alignItems={`flex-start`} gap={1} width="100%" maxWidth={`100%`}>
        {isBase ? <ProductBadges tags={product?.tags} showPlaceholder={true} /> : null}
        <Link href={link.url} size={`lg`} fontWeight={600}>
          {product?.title}
        </Link>
        <ProductPrice variant={parsedSelectedSize} direction={`row`} />
        {!isBase ? (
          <Box display="flex" gap={1} maxWidth={`100%`} width="100%">
            <Select backgroundColor="white" width="100%" onChange={e => setSelectedSize(e?.target?.value)}>
              {sizes?.map(size => (
                <option key={size?.id} value={size?.id}>
                  {size?.size}
                </option>
              ))}
            </Select>
            <ProductQuantity value={quantity} min={1} onIncrement={onIncrement} onDecrement={onDecrement} size="md" />
          </Box>
        ) : null}
        {isBase ? (
          <Button
            width={"100%"}
            variant={`green`}
            onClick={() => setQuickViewProduct(product)}
            isLoading={cartLoading}
            isDisabled={selectedProduct[0]?.availableForSale == false ? true : false}
          >
            {selectedProduct[0]?.availableForSale == false ? "UNAVAILABLE" : "QUICK ADD"}
          </Button>
        ) : (
          <Button
            width={"100%"}
            variant={`green`}
            onClick={handleAddToCart}
            isLoading={cartLoading}
            isDisabled={selectedProduct[0]?.availableForSale == false ? true : false}
          >
            {selectedProduct[0]?.availableForSale == false ? "UNAVAILABLE" : "Add To Cart"}
          </Button>
        )}
      </VStack>
      {quickViewProduct && (
        <ProductCardDrawer
          product={quickViewProduct}
          updateQuickViewProduct={product => setQuickViewProduct(product)}
          onClose={() => setQuickViewProduct(null)}
        />
      )}
    </Stack>
  )
}
